import { useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import { getCommentAttention } from "./commentAttention";
import {
	chunkMergeRequests,
	fetchDiscussionBatch,
	runWithConcurrency,
} from "./mergeRequestDiscussions";

// Per-MR classification cache. A new note bumps updatedAt and userNotesCount,
// and a resolve changes the unresolved count, so the key goes stale exactly
// when the classification might change. Re-entering the view or a two-minute
// poll then refetches only the MRs that moved. Bounded with oldest-first
// eviction; cleared on logout.
const attentionCache = new Map();
const MAX_CACHE_ENTRIES = 1000;

const cacheKey = (mr) =>
	`${mr.id}:${mr.updatedAt}:${mr.userNotesCount ?? ""}:${mr.discussions ?? ""}`;

const cacheAttention = (key, attention) => {
	attentionCache.delete(key);
	attentionCache.set(key, attention);
	while (attentionCache.size > MAX_CACHE_ENTRIES) {
		attentionCache.delete(attentionCache.keys().next().value);
	}
};

export const clearCommentAttentionCache = () => {
	attentionCache.clear();
};

// An MR with no user notes has no human comment to answer, so skip the
// discussions fetch. userNotesCount is absent on partially enriched MRs; those
// are fetched to stay safe.
const mayNeedResponse = (mr) => mr.userNotesCount !== 0;

const byLatestActivity = (a, b) =>
	new Date(b.commentAttention.latest.createdAt) -
	new Date(a.commentAttention.latest.createdAt);

// How long a background load may wait for the browser to go idle before it
// starts anyway.
const IDLE_START_TIMEOUT_MS = 3000;

const whenIdle = (callback) => {
	if (typeof window.requestIdleCallback === "function") {
		const handle = window.requestIdleCallback(callback, {
			timeout: IDLE_START_TIMEOUT_MS,
		});
		return () => window.cancelIdleCallback(handle);
	}
	const handle = setTimeout(callback, 1);
	return () => clearTimeout(handle);
};

// `enabled` gates fetching entirely (e.g. until the MR list has loaded).
// `pollKey` (e.g. the MR list's lastUpdated) re-runs the load on every poll so
// MRs that failed to fetch are retried; cached MRs make no request.
// `background` defers each load until the browser is idle, so the inbox and
// its count warm up without competing with the view the user is looking at;
// switching to the inbox (background false) starts a deferred load at once.
export const useCommentAttention = (
	mrs,
	enabled,
	{ background = false, pollKey } = {},
) => {
	const { user } = useAuth();
	const token = user?.access_token;
	const viewerUsername = user?.profile?.username;
	const [state, setState] = useState({
		items: [],
		loading: false,
		pending: 0,
		unchecked: 0,
		error: null,
	});
	const [refreshKey, setRefreshKey] = useState(0);
	const forceRefreshRef = useRef(false);
	const mrsRef = useRef(mrs);
	mrsRef.current = mrs;
	const backgroundRef = useRef(background);
	backgroundRef.current = background;
	const startNowRef = useRef(null);
	const signature = enabled ? mrs.map(cacheKey).join("|") : "";

	// biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey and pollKey intentionally trigger reloads.
	useEffect(() => {
		if (!enabled || !token) return;

		const eligible = mrsRef.current.filter(mayNeedResponse);
		const force = forceRefreshRef.current;
		forceRefreshRef.current = false;
		const controller = new AbortController();

		const collect = () => {
			const items = [];
			for (const mr of eligible) {
				const attention = attentionCache.get(cacheKey(mr));
				if (attention) items.push({ ...mr, commentAttention: attention });
			}
			return items.sort(byLatestActivity);
		};

		// Most recently updated first, so the top of the inbox fills in first.
		const stale = eligible
			.filter((mr) => force || !attentionCache.has(cacheKey(mr)))
			.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

		if (stale.length === 0) {
			setState({
				items: collect(),
				loading: false,
				pending: 0,
				unchecked: 0,
				error: null,
			});
			return;
		}

		// A forced refresh marks every MR stale but keeps the cached results on
		// screen while they reload.
		const hadResults = eligible.some((mr) => attentionCache.has(cacheKey(mr)));
		let remaining = stale.length;
		let failures = 0;
		let unclassified = 0;
		let lastError = null;
		// Only block the view with a skeleton when nothing is known yet. A retry
		// after a total failure keeps its error banner up until something loads,
		// rather than flashing the skeleton on every poll.
		setState((previous) => ({
			items: collect(),
			loading: !hadResults && !previous.error,
			pending: remaining,
			unchecked: 0,
			error: hadResults ? null : previous.error,
		}));

		const load = async () => {
			await runWithConcurrency(
				chunkMergeRequests(stale).map((batch) => async () => {
					try {
						const discussionsByMr = await fetchDiscussionBatch(
							batch,
							token,
							controller.signal,
						);
						for (const [index, mr] of batch.entries()) {
							// Isolate each MR: a single thread that cannot be classified
							// (e.g. too long to fully page) must not discard every other
							// correctly classified MR in the batch. Identify the viewer
							// explicitly rather than assuming mr.author is the viewer.
							try {
								cacheAttention(
									cacheKey(mr),
									getCommentAttention(
										discussionsByMr[index],
										viewerUsername ?? mr.author.username,
									),
								);
							} catch (mrError) {
								unclassified++;
								console.warn(
									`Skipping comment classification for MR ${mr.id}: ${mrError.message}`,
								);
							}
						}
					} catch (error) {
						if (controller.signal.aborted) return;
						failures += batch.length;
						lastError = error;
						console.warn(
							`Skipping comment activity for ${batch.length} MRs: ${error.message}`,
						);
					}
					if (controller.signal.aborted) return;
					// Publish after every batch so results stream in instead of
					// waiting on the slowest request.
					remaining -= batch.length;
					// Until a batch succeeds there is nothing new to show: leave the
					// skeleton or the previous error in place.
					if (!hadResults && failures === stale.length - remaining) {
						setState((previous) => ({ ...previous, pending: remaining }));
						return;
					}
					setState({
						items: collect(),
						loading: false,
						pending: remaining,
						// Never fetched or not classifiable: the view must not claim
						// "all caught up" while any of these remain.
						unchecked: failures + unclassified,
						error: null,
					});
				}),
			);
			if (controller.signal.aborted) return;
			// Surface an error only when every fetch failed. Partial failures keep
			// the classified results and report `unchecked`; failed MRs are not
			// cached, so the next poll (pollKey) or refresh fetches just them.
			if (failures === stale.length && !hadResults) {
				setState({
					items: [],
					loading: false,
					pending: 0,
					unchecked: 0,
					error: lastError?.message || "Failed to load comment activity",
				});
			}
		};

		if (!backgroundRef.current) {
			load();
			return () => controller.abort();
		}

		let started = false;
		const start = () => {
			if (started) return;
			started = true;
			startNowRef.current = null;
			load();
		};
		startNowRef.current = start;
		const cancelIdle = whenIdle(start);
		return () => {
			cancelIdle();
			if (startNowRef.current === start) startNowRef.current = null;
			controller.abort();
		};
	}, [enabled, pollKey, refreshKey, signature, token, viewerUsername]);

	// Opening the inbox while a background load still waits for idle time
	// starts it immediately, without restarting fetches already in flight.
	useEffect(() => {
		if (!background) startNowRef.current?.();
	}, [background]);

	return {
		...state,
		refetch: () => {
			if (!enabled) return;
			forceRefreshRef.current = true;
			setRefreshKey((key) => key + 1);
		},
	};
};
